import * as Icons from "@nova/component-icons"
import { InspectorPanelPlugin } from "../../useInspectorPanel"
import { ButtonBorderless } from "@nova/component-btn"
import { LayerPluginSpec, PluginSpec } from "plugins/plugin-registry"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ReactNode } from "react"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useIsEditor } from "tools/hooks/useIsEditor"

export type InspectorPanelFooterAction = {
    leftIcon?: Icons.IconComponent
    rightIcon?: Icons.IconComponent
    text?: string
    onClick: () => void
    isDestructive?: boolean
}

export function getGenericUseFooterActions(spec: LayerPluginSpec) {
    const useFooterActions = (id: string) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const confirmationModal = useConfirmationModalContext()
        const isEditor = useIsEditor()
        if (!isEditor) return []
        return [
            {
                text: "Delete",
                onClick: async () => {
                    if (spec.confirmDelete(entity)) {
                        const didConfirm = await confirmationModal.confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: "Delete Selection?",
                                message: `This will delete ${entity.name}, do you want to proceed?`,
                            }),
                        })
                        if (!didConfirm) return
                    }
                    dispatch(spec.deleteAction({ id }))
                },
                rightIcon: Icons.Trash,
                isDestructive: true,
            },
        ]
    }
    return useFooterActions
}

export function getGenericDuplicatableFooterActions(spec: PluginSpec) {
    const useFooterActions = (id: string) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const confirmationModal = useConfirmationModalContext()
        const isEditor = useIsEditor()
        if (!isEditor) return []
        const duplicate = spec.duplicate
        return [
            ...(duplicate
                ? [
                      {
                          text: "Duplicate",
                          onClick: () => duplicate(entity),
                          leftIcon: Icons.Copy,
                      },
                  ]
                : []),
            {
                text: "Delete",
                onClick: async () => {
                    if (spec.confirmDelete(entity)) {
                        const didConfirm = await confirmationModal.confirm({
                            RenderComponent: getGenericConfirmationModalComponent({
                                title: "Delete Selection?",
                                message: `This will delete ${entity.name}, do you want to proceed?`,
                            }),
                        })
                        if (!didConfirm) return
                    }
                    dispatch(spec.deleteAction({ id }))
                },
                rightIcon: Icons.Trash,
                isDestructive: true,
            },
        ]
    }
    return useFooterActions
}

export function getGenericInspectorPanelFooter() {
    const GenericInspectorPanelFooter = ({
        id,
        plugin,
    }: {
        id: string
        plugin: InspectorPanelPlugin
    }) => {
        const actions = plugin.useFooterActions(id)
        if (!actions.length) return null
        return (
            <GenericInspectorPanelFooterContainer>
                <div className="flex flex-row-reverse justify-between items-center">
                    {actions.reverse().map((action, i) => (
                        <ButtonBorderless
                            key={i}
                            isDestructive={action.isDestructive}
                            title={action.text}
                            RightIcon={action.rightIcon}
                            LeftIcon={action.leftIcon}
                            onClick={action.onClick}
                        />
                    ))}
                </div>
            </GenericInspectorPanelFooterContainer>
        )
    }
    return GenericInspectorPanelFooter
}

export function GenericInspectorPanelFooterContainer({ children }: { children: ReactNode }) {
    return <div className="border-t border-t-gray-700 p-2">{children}</div>
}
